import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DoorApi, DoorCredentialSource } from '../services/DoorApiClient';
import { connectDoorSocket } from '../services/doorSocket';
import { isApiError } from '../utils/apiErrors';
import { errorMessageOf } from '../utils/apiErrors';
import type { DoorCounts, DoorFavour, DoorFilter, DoorGuest, WalkInInput } from '../types/Door';
import { newKey } from '../utils/newKey';

/** loading: first load; ready: showing data; ended: credentials refused; missing: no such event; unavailable: the server cannot serve it. */
export type DoorStatus = 'loading' | 'ready' | 'ended' | 'missing' | 'unavailable';

export const DOOR_POLL_MS = 10_000;
/** With a live socket the poll is only a safety net. */
export const DOOR_POLL_LIVE_MS = 60_000;
const EMPTY_COUNTS: DoorCounts = { expected: 0, checked_in: 0, walk_ins: 0, not_arrived: 0, present: 0 };

export function statusOfError(err: unknown): DoorStatus {
  if (isApiError(err)) {
    if (err.status === 401 || err.status === 403) return 'ended';
    if (err.status === 404) return 'missing';
  }
  return 'unavailable';
}

/** Replaces one guest by id and returns a new array (never mutates). A guest not in the list is added. */
export function withGuest(list: readonly DoorGuest[], guest: DoorGuest): DoorGuest[] {
  const i = list.findIndex((g) => g.id === guest.id);
  if (i < 0) return [...list, guest];
  return [...list.slice(0, i), guest, ...list.slice(i + 1)];
}

/** Replaces one favour by id (new array); a favour not in the list is added. */
export function withFavour(list: readonly DoorFavour[], favour: DoorFavour): DoorFavour[] {
  const i = list.findIndex((f) => f.id === favour.id);
  if (i < 0) return [...list, favour];
  return [...list.slice(0, i), favour, ...list.slice(i + 1)];
}

const norm = (s: string): string => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

export function filterGuests(guests: readonly DoorGuest[], q: string, filter: DoorFilter): DoorGuest[] {
  const needle = norm(q.trim());
  return guests.filter((g) => {
    if (filter === 'checked_in' && !g.checked_in) return false;
    if (filter === 'not_arrived' && (g.checked_in || g.walk_in || g.rsvp_status === 'declined')) return false;
    if (filter === 'walk_in' && !g.walk_in) return false;
    if (!needle) return true;
    return norm(g.name).includes(needle) || g.tags.some((t) => norm(t).includes(needle));
  });
}

export interface UseDoor {
  status: DoorStatus;
  error: string | null;
  /** True when the last refresh failed but a previous list is still shown (flaky network). */
  stale: boolean;
  /** True while the live socket is connected. */
  live: boolean;
  guests: DoorGuest[];
  favours: DoorFavour[];
  counts: DoorCounts;
  busyId: string | null;
  actionError: string | null;
  refresh: () => Promise<void>;
  checkIn: (guestId: string) => Promise<void>;
  undoCheckIn: (guestId: string) => Promise<void>;
  addWalkIn: (input: WalkInInput) => Promise<DoorGuest | null>;
  giveFavour: (favourId: string) => Promise<void>;
  takeBackFavour: (favourId: string) => Promise<void>;
  clearActionError: () => void;
}

/**
 * The door's data and actions for one event. Polls while the tab is visible (the fallback that keeps devices in step whether
 * or not a live socket is up) and again when the network comes back. A failed refresh keeps the last list on screen.
 */
export function useDoor(api: DoorApi, eventId: string | null, pollMs: number = DOOR_POLL_MS, credentials?: DoorCredentialSource): UseDoor {
  const [status, setStatus] = useState<DoorStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [guests, setGuests] = useState<DoorGuest[]>([]);
  const [favours, setFavours] = useState<DoorFavour[]>([]);
  const [counts, setCounts] = useState<DoorCounts>(EMPTY_COUNTS);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const loaded = useRef(false);
  const inFlight = useRef(false);
  const alive = useRef(true);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const refresh = useCallback(async () => {
    if (!eventId || inFlight.current) return;
    inFlight.current = true;
    try {
      // Favours are a side list: if they cannot load the door still works, and the next refresh tries again.
      const [list, c, fav] = await Promise.all([api.listGuests(eventId), api.counts(eventId), api.listFavours(eventId).catch(() => null)]);
      if (!alive.current) return;
      loaded.current = true;
      setGuests(list); setCounts(c); if (fav) setFavours(fav); setStatus('ready'); setError(null); setStale(false);
    } catch (err) {
      if (!alive.current) return;
      const next = statusOfError(err);
      const offline = isApiError(err) && err.offline;
      // Flaky network or a server blip with a list already on screen: keep it and say it may be out of date.
      if (loaded.current && (offline || next === 'unavailable')) { setStale(true); setError(errorMessageOf(err, 'Could not refresh.')); return; }
      setStatus(next); setError(errorMessageOf(err, 'Could not load the guest list.'));
    } finally {
      inFlight.current = false;
    }
  }, [api, eventId]);

  // New event: start clean and load.
  useEffect(() => {
    loaded.current = false;
    setStatus('loading'); setGuests([]); setFavours([]); setCounts(EMPTY_COUNTS); setStale(false); setError(null);
    if (eventId) void refresh();
  }, [eventId, refresh]);

  // Poll while the tab is visible, and again when the network or the tab comes back. Slow when the live socket is up.
  const effectivePoll = pollMs > 0 && live ? Math.max(pollMs, DOOR_POLL_LIVE_MS) : pollMs;
  useEffect(() => {
    if (!eventId) return undefined;
    const tick = () => { if (typeof document === 'undefined' || !document.hidden) void refresh(); };
    const timer = effectivePoll > 0 ? setInterval(tick, effectivePoll) : undefined;
    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener('online', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [eventId, effectivePoll, refresh]);

  // Live updates: merge the guest at once, then refresh for the counts (coalesced).
  useEffect(() => {
    setLive(false);
    const creds = credentials?.();
    if (!eventId || !creds || pollMs === 0) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const close = connectDoorSocket(creds, eventId, {
      onConnection: (c) => { if (alive.current) setLive(c); if (c) void refresh(); },
      onChange: (change) => {
        setGuests((g) => withGuest(g, change.guest));
        if (change.favour) setFavours((f) => withFavour(f, change.favour!));
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => { void refresh(); }, 400);
      },
    });
    return () => { if (timer) clearTimeout(timer); close(); setLive(false); };
  }, [eventId, credentials, pollMs, refresh]);

  const run = useCallback(async (guestId: string | null, fn: () => Promise<void>) => {
    setBusyId(guestId); setActionError(null);
    try { await fn(); } catch (err) {
      if (!alive.current) return;
      setActionError(errorMessageOf(err, 'That did not go through. Try again.'));
      if (isApiError(err) && (err.status === 401 || err.status === 403)) setStatus('ended');
    } finally { if (alive.current) setBusyId(null); }
  }, []);

  const checkIn = useCallback((guestId: string) => run(guestId, async () => {
    if (!eventId) return;
    const out = await api.checkIn(eventId, guestId);
    setGuests((g) => withGuest(g, out.guest)); setCounts(out.counts);
  }), [api, eventId, run]);

  const undoCheckIn = useCallback((guestId: string) => run(guestId, async () => {
    if (!eventId) return;
    const out = await api.undoCheckIn(eventId, guestId);
    setGuests((g) => withGuest(g, out.guest)); setCounts(out.counts);
  }), [api, eventId, run]);

  const addWalkIn = useCallback(async (input: WalkInInput): Promise<DoorGuest | null> => {
    let added: DoorGuest | null = null;
    await run(null, async () => {
      if (!eventId) return;
      const out = await api.addWalkIn(eventId, input);
      added = out.guest;
      setGuests((g) => withGuest(g, out.guest)); setCounts(out.counts);
    });
    return added;
  }, [api, eventId, run]);

  // One idempotency key per favour per tap session: a retry of the same hand-over never takes stock twice.
  const keys = useRef(new Map<string, string>());
  const giveFavour = useCallback((favourId: string) => run(favourId, async () => {
    if (!eventId) return;
    const key = keys.current.get(favourId) ?? newKey();
    keys.current.set(favourId, key);
    const out = await api.handOver(eventId, favourId, key);
    keys.current.delete(favourId);
    setFavours((f) => withFavour(f, out.favour));
  }), [api, eventId, run]);

  const takeBackFavour = useCallback((favourId: string) => run(favourId, async () => {
    if (!eventId) return;
    const out = await api.undoHandOver(eventId, favourId);
    setFavours((f) => withFavour(f, out.favour));
  }), [api, eventId, run]);

  return useMemo(() => ({
    status, error, stale, live, guests, favours, counts, busyId, actionError, refresh, checkIn, undoCheckIn, addWalkIn, giveFavour, takeBackFavour,
    clearActionError: () => setActionError(null),
  }), [status, error, stale, live, guests, favours, counts, busyId, actionError, refresh, checkIn, undoCheckIn, addWalkIn, giveFavour, takeBackFavour]);
}
